import type { FC, ReactNode } from 'react'
import type { PostSummary } from '@/types'

import Link from 'next/link'
import { BrandMark } from '@/components/generals/brand-mark'
import { PostMeta } from '@/components/journal/post-meta'

interface FeaturedStoryProps {
    post: PostSummary
}

/* The arched cover of the featured post with its title card, used in the home page hero. */
export const FeaturedStory: FC<FeaturedStoryProps> = ({ post }): ReactNode => {
    const href = `/journal/${post.slug}`

    return (
        <div className='relative mx-auto w-full max-w-md pb-14 lg:max-w-none lg:pb-10'>
            <div
                aria-hidden='true'
                className='absolute -top-4 -right-4 bottom-24 left-8 rounded-t-full border border-primary/30 sm:-top-6 sm:-right-6'
            />

            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='group relative block aspect-[4/5] overflow-hidden rounded-t-full rounded-b-3xl bg-muted'
            >
                {post.cover ? (
                    <img
                        src={post.cover}
                        alt={post.cover_alt ?? ''}
                        fetchPriority='high'
                        decoding='async'
                        className='size-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03] motion-reduce:transition-none'
                    />
                ) : (
                    <span className='flex size-full items-center justify-center text-primary/35'>
                        <BrandMark className='size-24' />
                    </span>
                )}
            </Link>

            <div className='absolute bottom-0 left-4 w-[min(22rem,calc(100%-2rem))] rounded-2xl bg-card p-6 shadow-[0_30px_60px_-24px_rgb(43_37_33/0.35)] ring-1 ring-border/70 sm:-left-8 lg:-left-12'>
                <p className='text-[0.7rem] font-medium uppercase tracking-[0.22em] text-primary'>
                    Featured story
                </p>

                <h2 className='mt-3 font-heading text-[1.35rem] leading-snug tracking-tight'>
                    <Link
                        href={href}
                        className='decoration-foreground/30 decoration-1 underline-offset-[5px] hover:underline'
                    >
                        <span data-mdlfy-cms={`posts:${post.id}:title`}>{post.title}</span>
                    </Link>
                </h2>

                <PostMeta
                    className='mt-3'
                    postId={post.id}
                    publishedAt={post.published_at}
                    readMinutes={post.read_minutes}
                />
            </div>
        </div>
    )
}
